import { LoadingProgress } from '@issuemanager/apps/client/components';
import {
  useAtlasSemanticThemeTokens,
  type WithTheme,
} from '@issuemanager/apps/client/theme';
import {
  Box,
  Divider,
  Stack,
  styled,
  Typography,
  type BoxProps,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const GridContainer = styled(Box)<WithTheme<BoxProps>>(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(4),
}));

export interface IssueFormFieldsProps {
  fields: {
    source?: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    issueType: React.ReactNode;
    issueSubtype: React.ReactNode;
    impact: React.ReactNode;
    severity: React.ReactNode;
    dueDate: React.ReactNode;
    ownerId: React.ReactNode;
    govmapGroups: React.ReactNode;
    attachments?: React.ReactNode;
    classification?: React.ReactNode;
    recommendations?: React.ReactNode;
    rootCause?: React.ReactNode;
    includeInAuditReport?: React.ReactNode;
  };
  showTitle?: boolean;
  showSource?: boolean;
  shouldShowExtendedFields?: boolean;
  showControlDeficiencyExtraFields?: boolean;
  showAuditFindingExtraFields?: boolean;
  isTemplateLoading?: boolean;
}

export function IssueFormFields({
  fields,
  showTitle = false,
  showSource = false,
  shouldShowExtendedFields = false,
  showControlDeficiencyExtraFields = false,
  showAuditFindingExtraFields = false,
  isTemplateLoading = false,
}: IssueFormFieldsProps): React.JSX.Element {
  const semantic = useAtlasSemanticThemeTokens();
  const { t } = useTranslation('issue');

  return (
    <Stack spacing={4}>
      {showTitle && (
        <Typography variant='h3' fontWeight='medium'>
          {t('form.sections.assignments')}
        </Typography>
      )}

      <Stack spacing={3}>
        {showSource && fields.source}

        <Stack direction='row' spacing={4}>
          {fields.issueType && <Stack flex={1}>{fields.issueType}</Stack>}
          {fields.issueSubtype && <Stack flex={1}>{fields.issueSubtype}</Stack>}
        </Stack>
        <Divider
          orientation='horizontal'
          flexItem
          style={{ color: semantic.color.background.container.value }}
        />

        {shouldShowExtendedFields &&
          (isTemplateLoading ? (
            <LoadingProgress height={200} />
          ) : (
            <>
              <GridContainer>
                {fields.title}
                {fields.ownerId}
                {fields.severity}
                {(showControlDeficiencyExtraFields ||
                  showAuditFindingExtraFields) &&
                  fields.classification}
                {fields.govmapGroups}
                {fields.dueDate}
              </GridContainer>
              {fields.description}
              {fields.impact}
              {showAuditFindingExtraFields && fields.recommendations}
              {(showControlDeficiencyExtraFields ||
                showAuditFindingExtraFields) &&
                fields.rootCause}
              {showAuditFindingExtraFields && fields.includeInAuditReport}
              {fields.attachments}
            </>
          ))}
      </Stack>
    </Stack>
  );
}
